"use client";
import React from "react";
import { Button } from "~/components/v3/themed/Button";
import SponsorStar from "./SponsorStar";
import { GetSponsorPackageForEvent } from "../actions";
import { If } from "~/components/v3/Conditional";
import { GoLinkExternal } from "react-icons/go";
import Link from "next/link";
import { env } from "~/env.mjs";
import { SponsorshipPriceType, SponsorshipDurationType } from "@prisma/client";
import ReactMarkdown from "react-markdown";

const getPriceDisplay = (
    tier: NonNullable<GetSponsorPackageForEvent>["packageTiers"][number],
) => {
    switch (tier.priceType) {
        case SponsorshipPriceType.FIXED:
            return `$${tier.priceDollars}.${tier?.priceCents?.toString().padStart(2, "0")}`;
        case SponsorshipPriceType.CUSTOM_MINIMUM:
            return `From $${tier.minimumDollars}.${tier?.minimumCents?.toString().padStart(2, "0")}`;
        case SponsorshipPriceType.CUSTOM_OPEN:
            return "Custom pricing";
        default:
            return "";
    }
};

const getCoverageDisplay = (
    tier: NonNullable<GetSponsorPackageForEvent>["packageTiers"][number],
) => {
    switch (tier.durationType) {
        case SponsorshipDurationType.EVENT_COUNT:
            if (tier.durationValue === 1) return "1 event";
            return `${tier.durationValue} events in a month`;

        case SponsorshipDurationType.TIME_PERIOD:
            if (tier.durationValue === 1) return "1 event over 1 month";
            return `1 event over ${tier.durationValue} months`;

        case SponsorshipDurationType.CUSTOM:
            return "Custom";

        default:
            return "";
    }
};

export const sponsorPlanColors = ["#0051EE", "#602EF4", "#028959"]; // v3-O500, v3-purple, v3-M500

export const SponsorTierCard = React.memo(
    ({
        tier,
        asset,
        communitySlug,
        color,
        isSelected,
        onSelect,
    }: {
        tier: NonNullable<GetSponsorPackageForEvent>["packageTiers"][number];
        asset: string | null;
        communitySlug?: string;
        color: string;
        isSelected: boolean;
        onSelect: (tierId: string) => void;
    }) => {
        return (
            <div
                className={`flex h-full transform flex-col rounded-lg p-6 text-white transition-all duration-300 ease-in-out hover:scale-105 ${isSelected ? "border-4 border-v3-primary" : ""}`}
                style={{ backgroundColor: color }}
            >
                {/* Name & Price */}
                <div className="mb-4">
                    <h3 className="mb-2 text-xl font-bold">{tier.name}</h3>
                    <div className="flex flex-row items-center">
                        <p className="text-3xl font-bold">
                            {getPriceDisplay(tier)}
                        </p>
                    </div>
                </div>

                {/* What you're covering */}
                <div className="mb-6 flex flex-col gap-4">
                    <span className="font-semibold">What you're covering</span>
                    <div className="flex flex-row items-center gap-2">
                        <SponsorStar />
                        <span className="text-sm">
                            {getCoverageDisplay(tier)}
                        </span>
                    </div>
                </div>

                {/* Custom Markdown */}
                {(tier.customMarkdown as string) && (
                    <div className="mb-6">
                        <ReactMarkdown className="prose prose-invert text-sm">
                            {tier.customMarkdown}
                        </ReactMarkdown>
                    </div>
                )}

                {(tier.customAttendees as PrismaJson.CustomAttendees) && (
                    <div className="mb-6">
                        <p className="text-sm">
                            {(() => {
                                const attendees = tier.customAttendees as {
                                    minAmount: number;
                                    maxAmount: number;
                                    attendeeDisplayName: string;
                                };
                                return `${attendees.minAmount}-${attendees.maxAmount} ${attendees.attendeeDisplayName}`;
                            })()}
                        </p>
                    </div>
                )}

                {/* Asset Link */}
                <If condition={!!asset}>
                    <Link
                        href={`${env.NEXT_PUBLIC_ASSETS_URL}/community/${communitySlug}/sponsors/${asset}`}
                        target="_blank"
                        className="mb-4 flex cursor-pointer flex-row items-center justify-end gap-1 underline hover:text-v3-primary"
                    >
                        <GoLinkExternal className="mt-1 text-xs" /> See sponsor
                        deck
                    </Link>
                </If>

                {/* Select Button */}
                <Button
                    variant={isSelected ? "default" : "secondary"}
                    className="mt-auto w-full"
                    onClick={() => onSelect(tier.id)}
                >
                    {isSelected ? "Selected" : "Select"}
                </Button>
            </div>
        );
    },
);

SponsorTierCard.displayName = "SponsorTierCard";
